import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';
import { useField } from '@unform/core';

import {
  CustomInput,
  CustomTextArea,
  Label,
  InputContainer,
  PasswordField,
} from './styles';

const propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.number,
  multiline: PropTypes.bool,
  withPasswordToggle: PropTypes.bool,
};

const defaultProps = {
  width: 100,
  multiline: false,
  withPasswordToggle: false,
};

const Input = ({
  name,
  label,
  width,
  multiline,
  withPasswordToggle,
  ...rest
}) => {
  const inputRef = useRef(null);
  const { fieldName, defaultValue = '', registerField, error } = useField(name);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [fieldName, registerField]);

  function handleEyeIconClick() {
    setShowPassword(!showPassword);
  }

  /**
   * `min` em <input type="number"> não impede digitar: o navegador só marca o
   * campo como inválido, e o unform envia assim mesmo. Barrar a tecla é o que
   * impede o sinal de menos de aparecer no campo.
   */
  function handleKeyDown(event) {
    const bloqueiaNegativo = rest.type === 'number' && Number(rest.min) >= 0;
    if (bloqueiaNegativo && ['-', 'e', 'E', '+'].includes(event.key)) {
      event.preventDefault();
    }

    if (rest.onKeyDown) rest.onKeyDown(event);
  }

  function renderInput() {
    return (
      <CustomInput
        ref={inputRef}
        id={fieldName}
        defaultValue={rest.value ? null : defaultValue}
        width={100}
        {...rest}
        // Depois do spread, senão o `type` original volta a valer.
        type={showPassword ? 'text' : rest.type}
        onKeyDown={handleKeyDown}
      />
    );
  }

  function renderControl() {
    if (multiline) {
      return (
        <CustomTextArea
          ref={inputRef}
          id={fieldName}
          defaultValue={rest.value ? null : defaultValue}
          rows="5"
          {...rest}
        />
      );
    }

    if (!withPasswordToggle) return renderInput();

    return (
      <PasswordField showPassword={showPassword}>
        {renderInput()}
        {showPassword ? (
          <IoMdEye
            color="var(--ink-faint)"
            size={24}
            onClick={handleEyeIconClick}
          />
        ) : (
          <IoMdEyeOff
            color="var(--ink-faint)"
            size={24}
            onClick={handleEyeIconClick}
          />
        )}
      </PasswordField>
    );
  }

  return (
    <InputContainer width={width}>
      {label && <Label htmlFor={fieldName}>{label}</Label>}

      {renderControl()}

      {error && (
        <span style={{ color: 'var(--danger)', marginTop: 5, fontWeight: 500 }}>
          {error}
        </span>
      )}
    </InputContainer>
  );
};

Input.propTypes = propTypes;
Input.defaultProps = defaultProps;

export default Input;
